import { Flex, Space, Typography } from "antd";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import SearchFilter from "~components/SearchFilter/SearchFilter.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import ConductFilters from "~pages/Conducts/components/ConductFilters.tsx";
import { useDeleteConduct } from "~pages/Conducts/hooks/useDeleteConduct.ts";
import { useUpsertConduct } from "~pages/Conducts/hooks/useUpsertConduct.ts";
import type { ConductFilter } from "~pages/Conducts/types/conductFilter.ts";
import PermissionCheck from "../../Common/PermissionCheck";
import AddConductButton from "./components/AddConductButton.tsx";
import ConductList from "./components/ConductList.tsx";
import { useFetchAllConducts } from "./hooks/useFetchAllConducts.ts";
import type { SearchFilterParams } from "./types/searchFilterParams.ts";

const PermissionName = PermissionNames.CONDUCT;

const ConductsPage = () => {
  const { formatMessage } = useIntl();
  const [searchText, setSearchText] = useState("");
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();

  const [filter, setFilter] = useState<ConductFilter>({
    competition: null,
    season: null,
    conductType: null,
    status: null,
  });

  const [sortBy, setSortBy] = useState<string>("-added");

  const params = useMemo<SearchFilterParams>(
    () => ({
      include: ["competitions", "seasons", "persons", "fixtures", "entities"].join(","),
      ...(filter.competition && { competitionId: filter.competition.value }),
      ...(filter.season && { seasonId: filter.season.value }),
      ...(filter.status && { conductStatus: filter.status }),
      ...(filter.conductType && { conductType: filter.conductType.join(",") }),
      ...(searchText && { nameFullLocalContains: searchText }),
      limit: pageSize.toString(),
      offset: offset.toString(),
      sortBy,
    }),
    [filter, searchText, pageSize, offset, sortBy],
  );

  const { data, isLoading } = useFetchAllConducts(params);

  const { mutateAsync: upsertConduct, isPending: isUpsertConductInProcess } = useUpsertConduct(params);
  const { mutateAsync: deleteConduct, isPending: isDeleteConductInProcess } = useDeleteConduct(params);

  const isPageLoading = useMemo(
    () => isLoading || isDeleteConductInProcess || isUpsertConductInProcess,
    [isLoading, isUpsertConductInProcess, isDeleteConductInProcess],
  );

  const conducts = data?.data || [];

  return (
    <Flex vertical gap={8}>
      <Flex justify="space-between" align="center">
        <Typography.Title level={4}> {formatMessage({ id: "conduct" })} </Typography.Title>
        <Space>
          <ConductFilters filter={filter} onFilterChanged={setFilter} loading={isPageLoading} />
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <AddConductButton onAdd={upsertConduct} isAddingInProcess={isUpsertConductInProcess} />
          </PermissionCheck>
          <SearchFilter doSearch={setSearchText} placeholderIntlKey="conduct.search.person.name" style={{ minWidth: 250 }} />
        </Space>
      </Flex>
      <ConductList
        data={conducts}
        isLoading={isPageLoading}
        pagination={{
          currentPage: currentPage,
          handlePaginationChange: handlePaginationChange,
          offset: offset,
          pageSize: pageSize,
          pageSizeOptions,
        }}
        onConductUpsert={upsertConduct}
        onConductDelete={deleteConduct}
        isUpsertInProcess={isUpsertConductInProcess}
        onSortChange={setSortBy}
      />
    </Flex>
  );
};

export default ConductsPage;
